import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class TicketSalesService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/reservations`,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expirado o inválido
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminData');
          window.location.href = '/admin';
        }
        return Promise.reject(error);
      }
    );
  }

  // Crear una nueva reserva para venta de entradas
  async createTicketSaleReservation(reservationData) {
    try {
      const response = await this.api.post('/ticket-sale', reservationData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear la reserva de venta');
      throw new Error(errorMessage);
    }
  }

  // Validar código de acceso
  async validateAccessCode(code) {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/access-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Código inválido');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al validar el código de acceso');
      throw new Error(errorMessage);
    }
  }

  // Obtener información del evento asociado a un código de acceso
  // Este método ya no es necesario ya que validateAccessCode devuelve la información del evento
  async getEventByAccessCode(code) {
    try {
      // Usar el endpoint de validación que ya incluye la información del evento
      const validationResult = await this.validateAccessCode(code);
      if (validationResult.eventData) {
        return validationResult.eventData;
      } else {
        throw new Error('No se encontró información del evento para este código');
      }
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener información del evento');
      throw new Error(errorMessage);
    }
  }

  // Verificar disponibilidad de asientos
  async checkSeatAvailability(eventId, seatIds) {
    try {
      const response = await this.api.post('/check-seat-availability', {
        eventId,
        seatIds
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al verificar disponibilidad de asientos');
      throw new Error(errorMessage);
    }
  }

  // Calcular precio total de los asientos seleccionados
  async calculateTotalPrice(eventId, seatIds) {
    try {
      const response = await this.api.post('/calculate-price', {
        eventId,
        seatIds
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al calcular el precio total');
      throw new Error(errorMessage);
    }
  }

  // Obtener límite de tickets por código de acceso
  async getTicketLimit(accessCodeId) {
    try {
      const response = await this.api.get(`/access-code/${accessCodeId}/ticket-limit`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener límite de tickets');
      throw new Error(errorMessage);
    }
  }

  // Confirmar venta de entradas
  async confirmTicketSale(saleData) {
    try {
      const response = await this.api.post('/confirm-sale', saleData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al confirmar la venta');
      throw new Error(errorMessage);
    }
  }

  // Obtener historial de ventas
  async getSalesHistory(params = {}) {
    try {
      const response = await this.api.get('/sales-history', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener historial de ventas');
      throw new Error(errorMessage);
    }
  }

  // Obtener estadísticas de ventas
  async getSalesStats(eventId = null) {
    try {
      const params = eventId ? { eventId } : {};
      const response = await this.api.get('/sales-stats', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener estadísticas de ventas');
      throw new Error(errorMessage);
    }
  }

  // Cancelar venta de entradas
  async cancelTicketSale(saleId, reason = '') {
    try {
      const response = await this.api.post(`/cancel-sale/${saleId}`, { reason });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cancelar la venta');
      throw new Error(errorMessage);
    }
  }

  // Generar comprobante de venta
  async generateSaleReceipt(saleId) {
    try {
      const response = await this.api.get(`/sale-receipt/${saleId}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al generar el comprobante');
      throw new Error(errorMessage);
    }
  }

  // Obtener mensaje de error personalizado
  getErrorMessage(error, defaultMessage) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  }
}

const ticketSalesService = new TicketSalesService();
export default ticketSalesService; 